import React, { useState, useMemo } from 'react';
import { Filter, Search } from 'lucide-react';

const TYPE_LABELS = {
  sign_change: '➜ राशि',
  nakshatra_change: '✦ नक्षत्र',
  pada_change: '◆ पाद',
  retrograde_start: '↺ वक्री प्रारंभ',
  retrograde_end: '↻ मार्गी',
  combust_start: '🔥 अस्त प्रारंभ',
  combust_end: '☀ अस्त समाप्त',
};

const TYPE_COLOR = {
  sign_change: 'bg-purple-500/20 border-purple-400/40 text-purple-200',
  nakshatra_change: 'bg-indigo-500/20 border-indigo-400/40 text-indigo-200',
  pada_change: 'bg-cyan-500/15 border-cyan-400/30 text-cyan-200',
  retrograde_start: 'bg-amber-500/20 border-amber-400/40 text-amber-200',
  retrograde_end: 'bg-emerald-500/20 border-emerald-400/40 text-emerald-200',
  combust_start: 'bg-red-500/20 border-red-400/40 text-red-200',
  combust_end: 'bg-orange-500/15 border-orange-400/30 text-orange-200',
};

const FILTERS = [
  { k: 'all', l: 'सभी' },
  { k: 'sign_change', l: 'राशि' },
  { k: 'nakshatra_change', l: 'नक्षत्र' },
  { k: 'pada_change', l: 'पाद' },
  { k: 'retrograde', l: 'वक्री' },
  { k: 'combust', l: 'अस्त' },
];

const SLOW = new Set(['Jupiter', 'Saturn', 'Rahu', 'Ketu']);
const FAST = new Set(['Sun', 'Moon', 'Mars', 'Mercury', 'Venus']);

export default function TimelineView({ timeline, onClickEvent }) {
  const [filterType, setFilterType] = useState('all');
  const [planetFilter, setPlanetFilter] = useState('all'); // all / slow / fast
  const [search, setSearch] = useState('');

  const events = useMemo(() => {
    let evs = timeline?.events || [];
    if (filterType !== 'all') {
      if (filterType === 'retrograde') {
        evs = evs.filter(e => e.event_type === 'retrograde_start' || e.event_type === 'retrograde_end');
      } else if (filterType === 'combust') {
        evs = evs.filter(e => e.event_type === 'combust_start' || e.event_type === 'combust_end');
      } else {
        evs = evs.filter(e => e.event_type === filterType);
      }
    }
    if (planetFilter === 'slow') evs = evs.filter(e => SLOW.has(e.planet_en));
    if (planetFilter === 'fast') evs = evs.filter(e => FAST.has(e.planet_en));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      evs = evs.filter(e =>
        (e.planet_hi || '').toLowerCase().includes(q) ||
        (e.planet_en || '').toLowerCase().includes(q) ||
        (e.from_label_hi || '').toLowerCase().includes(q) ||
        (e.to_label_hi || '').toLowerCase().includes(q) ||
        (e.dt_ist || '').toLowerCase().includes(q)
      );
    }
    return evs;
  }, [timeline, filterType, planetFilter, search]);

  if (!timeline || !timeline.events) return null;

  return (
    <div data-testid="timeline-view" className="space-y-4">
      {/* Window meta */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-text-muted">रेंज:</span>
        <span className="font-mono text-gold">{timeline.window_start_ist}</span>
        <span className="text-text-muted">→</span>
        <span className="font-mono text-gold">{timeline.window_end_ist}</span>
        <span className="text-text-muted">·</span>
        <span className="text-text-muted">कुल घटनाएँ:</span>
        <span className="font-mono text-gold">{timeline.total_events}</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-text-muted" />
        {FILTERS.map(f => (
          <button
            key={f.k}
            data-testid={`tl-filter-${f.k}`}
            onClick={() => setFilterType(f.k)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${filterType === f.k
              ? 'bg-gold/25 border-gold/60 text-gold'
              : 'bg-white/5 border-white/10 text-text-muted hover:text-gold'}`}
          >{f.l}</button>
        ))}
        <span className="text-text-muted mx-2">|</span>
        {[{ k: 'all', l: 'सभी ग्रह' }, { k: 'slow', l: 'मंद' }, { k: 'fast', l: 'शीघ्र' }].map(f => (
          <button
            key={f.k}
            data-testid={`tl-planet-${f.k}`}
            onClick={() => setPlanetFilter(f.k)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${planetFilter === f.k
              ? 'bg-gold/25 border-gold/60 text-gold'
              : 'bg-white/5 border-white/10 text-text-muted hover:text-gold'}`}
          >{f.l}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-text-muted" />
          <input
            data-testid="tl-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="खोजें (ग्रह / राशि / नक्षत्र / दिनांक)"
            className="bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs text-text-main placeholder:text-text-muted focus:border-gold/50 outline-none w-64"
          />
        </div>
      </div>

      {/* Event list — virtualized-style scrollable */}
      <div className="rounded-xl border border-gold/20 max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-gold/80 bg-purple-950/40 sticky top-0 z-10 backdrop-blur">
            <tr>
              <th className="text-left py-2 px-3">दिनांक / समय (IST)</th>
              <th className="text-left py-2 px-3">ग्रह</th>
              <th className="text-left py-2 px-3">घटना</th>
              <th className="text-left py-2 px-3">परिवर्तन</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={4} className="py-6 text-center text-text-muted">कोई घटना नहीं मिली</td></tr>
            )}
            {events.slice(0, 500).map((e, i) => (
              <tr
                key={i}
                data-testid={`tl-event-${i}`}
                onClick={() => onClickEvent && onClickEvent(e)}
                className="border-t border-white/5 hover:bg-white/5 cursor-pointer"
              >
                <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">{e.dt_ist}</td>
                <td className="py-2 px-3 font-semibold text-gold">{e.planet_hi}</td>
                <td className="py-2 px-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${TYPE_COLOR[e.event_type] || 'bg-white/5 border-white/10'}`}>
                    {TYPE_LABELS[e.event_type] || e.event_type}
                  </span>
                </td>
                <td className="py-2 px-3 text-xs">
                  <span className="text-text-muted">{e.from_label_hi}</span>
                  <span className="mx-1 text-gold">→</span>
                  <span className="text-text-main">{e.to_label_hi}</span>
                </td>
              </tr>
            ))}
            {events.length > 500 && (
              <tr><td colSpan={4} className="py-3 text-center text-xs text-text-muted">… {events.length - 500} additional events not shown (filter to narrow)</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
